import type { PerformanceData } from './types';

export class WebVitalsCollector {
  private data: PerformanceData = {};
  private observers: PerformanceObserver[] = [];
  private installed = false;

  install(): void {
    if (this.installed) return;
    this.installed = true;

    this.captureNavigationTiming();
    this.observeLCP();
    this.observeFID();
    this.observeCLS();
  }

  private captureNavigationTiming(): void {
    try {
      const entries = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];
      if (entries.length > 0) {
        this.data.ttfb_ms = Math.round(entries[0].responseStart - entries[0].requestStart);
      }
    } catch {
      // PerformanceNavigationTiming not supported
    }
  }

  private observeLCP(): void {
    try {
      const observer = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        if (entries.length > 0) {
          this.data.lcp_ms = Math.round(entries[entries.length - 1].startTime);
        }
      });
      observer.observe({ type: 'largest-contentful-paint', buffered: true });
      this.observers.push(observer);
    } catch {
      // LCP not supported
    }
  }

  private observeFID(): void {
    try {
      const observer = new PerformanceObserver((list) => {
        const entries = list.getEntries() as PerformanceEventTiming[];
        if (entries.length > 0) {
          this.data.fid_ms = Math.round(entries[0].processingStart - entries[0].startTime);
        }
      });
      observer.observe({ type: 'first-input', buffered: true });
      this.observers.push(observer);
    } catch {
      // FID not supported
    }
  }

  private observeCLS(): void {
    try {
      let clsValue = 0;
      const observer = new PerformanceObserver((list) => {
        for (const entry of list.getEntries() as any[]) {
          if (!entry.hadRecentInput) {
            clsValue += entry.value;
          }
        }
        this.data.cls = Math.round(clsValue * 10000) / 10000;
      });
      observer.observe({ type: 'layout-shift', buffered: true });
      this.observers.push(observer);
    } catch {
      // CLS not supported
    }
  }

  getData(): PerformanceData {
    const result = { ...this.data };

    try {
      const mem = (performance as any).memory;
      if (mem) {
        result.memory_used_mb = Math.round((mem.usedJSHeapSize / 1048576) * 100) / 100;
      }
    } catch {
      // memory API not available
    }

    return result;
  }

  destroy(): void {
    for (const obs of this.observers) {
      obs.disconnect();
    }
    this.observers = [];
    this.installed = false;
  }
}
